<script setup>
// Formulário do contrato novo: escolhe a proposta (valores congelados vêm
// dela), preenche quem contrata e as condições. O backend valida CPF/CNPJ e
// devolve os campos inválidos, que voltam marcados aqui.
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ContractsAPI from 'dashboard/api/contracts';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { formatBRL } from './contractFormat';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
  proposals: { type: Array, required: true },
  defaultClientName: { type: String, default: '' },
});

const emit = defineEmits(['created']);

const { t } = useI18n();

const today = new Date();
const isoToday = [
  today.getFullYear(),
  String(today.getMonth() + 1).padStart(2, '0'),
  String(today.getDate()).padStart(2, '0'),
].join('-');

const proposalId = ref(props.proposals[0]?.id ?? null);
const contratante = ref({
  nome: props.proposals[0]?.client_name || props.defaultClientName,
  documento: '',
  endereco: '',
  bairro: '',
  cidade: '',
  uf: '',
  cep: '',
  email: '',
  telefone: '',
  representante_nome: '',
  representante_cpf: '',
});
const termos = ref({
  inicio: isoToday,
  dia_vencimento: String(Math.min(today.getDate(), 28)),
  forma_pagamento: 'boleto',
  clausulas_adicionais: '',
});
const invalid = ref([]);
const isSaving = ref(false);

// CNPJ tem 14 dígitos: aí o contrato é da empresa e pede quem assina por ela.
const isCompany = computed(
  () => contratante.value.documento.replace(/\D/g, '').length === 14
);

const CONTRATANTE_LAYOUT = computed(() => [
  { key: 'nome', span: 'col-span-2' },
  { key: 'documento', span: 'col-span-2 sm:col-span-1' },
  { key: 'telefone', span: 'col-span-2 sm:col-span-1' },
  ...(isCompany.value
    ? [
        { key: 'representante_nome', span: 'col-span-2 sm:col-span-1' },
        { key: 'representante_cpf', span: 'col-span-2 sm:col-span-1' },
      ]
    : []),
  { key: 'email', span: 'col-span-2' },
  { key: 'endereco', span: 'col-span-2' },
  { key: 'bairro', span: 'col-span-2 sm:col-span-1' },
  { key: 'cep', span: 'col-span-2 sm:col-span-1' },
  { key: 'cidade', span: 'col-span-2 sm:col-span-1' },
  { key: 'uf', span: 'col-span-2 sm:col-span-1' },
]);

const PAYMENT_METHODS = ['boleto', 'pix', 'cartao'];

const proposalLabel = proposal =>
  t('DEALS.CONTRACTS.FORM.PROPOSAL_OPTION', {
    id: proposal.id,
    client: proposal.client_name || proposal.title || '—',
    total: formatBRL(proposal.total),
  });

const fieldMessage = key =>
  invalid.value.includes(key) ? t('DEALS.CONTRACTS.FORM.INVALID') : '';
const fieldType = key => (invalid.value.includes(key) ? 'error' : 'info');

const selectClass =
  'w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 outline-none focus:border-n-brand';

const submit = async () => {
  isSaving.value = true;
  invalid.value = [];
  try {
    const { data } = await ContractsAPI.createContract({
      conversation_id: props.conversationId,
      proposal_id: proposalId.value,
      contratante: contratante.value,
      termos: termos.value,
    });
    emit('created', data);
  } catch (error) {
    invalid.value = error?.response?.data?.invalid_fields || [];
    useAlert(
      invalid.value.length
        ? t('DEALS.CONTRACTS.FORM.FIX_FIELDS')
        : t('DEALS.CONTRACTS.ALERTS.ERROR')
    );
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <form class="flex flex-col gap-5" @submit.prevent="submit">
    <div>
      <label
        for="contract-proposal"
        class="mb-1 block text-sm font-medium text-n-slate-12"
      >
        {{ t('DEALS.CONTRACTS.FORM.PROPOSAL') }}
      </label>
      <select id="contract-proposal" v-model="proposalId" :class="selectClass">
        <option
          v-for="proposal in proposals"
          :key="proposal.id"
          :value="proposal.id"
        >
          {{ proposalLabel(proposal) }}
        </option>
      </select>
      <p class="mt-1 text-xs text-n-slate-11">
        {{ t('DEALS.CONTRACTS.FORM.PROPOSAL_HINT') }}
      </p>
    </div>

    <fieldset class="grid grid-cols-2 gap-3">
      <legend class="col-span-2 mb-1 text-sm font-semibold text-n-slate-12">
        {{ t('DEALS.CONTRACTS.FORM.CONTRATANTE') }}
      </legend>
      <p class="col-span-2 -mt-1 text-xs text-n-slate-11">
        {{ t('DEALS.CONTRACTS.FORM.DOCUMENT_HINT') }}
      </p>
      <Input
        v-for="field in CONTRATANTE_LAYOUT"
        :key="field.key"
        v-model="contratante[field.key]"
        :class="field.span"
        size="sm"
        :type="field.key === 'email' ? 'email' : 'text'"
        :label="t(`DEALS.CONTRACTS.FORM.FIELDS.${field.key}`)"
        :message="fieldMessage(field.key)"
        :message-type="fieldType(field.key)"
      />
    </fieldset>

    <fieldset class="grid grid-cols-2 gap-3">
      <legend class="col-span-2 mb-1 text-sm font-semibold text-n-slate-12">
        {{ t('DEALS.CONTRACTS.FORM.TERMS') }}
      </legend>
      <Input
        v-model="termos.inicio"
        class="col-span-2 sm:col-span-1"
        size="sm"
        type="date"
        :label="t('DEALS.CONTRACTS.FORM.FIELDS.inicio')"
      />
      <Input
        v-model="termos.dia_vencimento"
        class="col-span-2 sm:col-span-1"
        size="sm"
        type="number"
        min="1"
        max="28"
        :label="t('DEALS.CONTRACTS.FORM.FIELDS.dia_vencimento')"
        :message="fieldMessage('dia_vencimento')"
        :message-type="fieldType('dia_vencimento')"
      />
      <div class="col-span-2">
        <label
          for="contract-payment"
          class="mb-1 block text-sm font-medium text-n-slate-12"
        >
          {{ t('DEALS.CONTRACTS.FORM.FIELDS.forma_pagamento') }}
        </label>
        <select
          id="contract-payment"
          v-model="termos.forma_pagamento"
          :class="selectClass"
        >
          <option
            v-for="method in PAYMENT_METHODS"
            :key="method"
            :value="method"
          >
            {{ t(`DEALS.CONTRACTS.FORM.PAYMENT.${method}`) }}
          </option>
        </select>
      </div>
      <TextArea
        v-model="termos.clausulas_adicionais"
        class="col-span-2"
        :max-length="3000"
        :label="t('DEALS.CONTRACTS.FORM.FIELDS.clausulas_adicionais')"
        :placeholder="t('DEALS.CONTRACTS.FORM.CLAUSES_PLACEHOLDER')"
      />
    </fieldset>

    <Button
      type="submit"
      :label="t('DEALS.CONTRACTS.FORM.SUBMIT')"
      :is-loading="isSaving"
      :disabled="!proposalId"
    />
  </form>
</template>
